import { Validators } from "@angular/forms";
import type { InputConfig, InputWidthType } from "../types/ez-form.types";
import type { InputSelectOption } from "../types/Input-select-option";

export interface GeneratedFormField {
  readonly name: string; readonly type: string; readonly control: string;
  readonly label: string; readonly labelToken: string; readonly required: boolean; readonly nullable: boolean;
  readonly minLength?: number; readonly maxLength?: number; readonly minimum?: number; readonly maximum?: number;
  readonly dateSemantics?: "calendar-date" | "utc-instant"; readonly lookupDomain?: string;
  readonly options?: readonly { readonly value: string; readonly label: string; readonly labelToken: string; readonly disabled?: boolean }[];
  readonly requiredWhen?: { readonly field: string; readonly equals: string };
  readonly visibleWhen?: { readonly field: string; readonly equals: string | number | boolean };
  readonly errorCodes: readonly string[];
  readonly readOnly?: boolean;
  /** Temporarily unavailable controls retain their values without granting edit permission. */
  readonly disabled?: boolean;
  /** Trusted installed metadata must explicitly permit persisted layout overrides. */
  readonly supportsLayout?: boolean;
  /** Trusted installed metadata must explicitly permit a declarative lookup source; absence grants no permission. */
  readonly supportsLookup?: boolean;
  /** Resolved presentation uses the existing input size and canonical numeric row group. */
  readonly width?: InputWidthType;
  readonly row?: number;
}
export interface GeneratedFormDefinition {
  readonly contract: string; readonly version: number; readonly fields: readonly GeneratedFormField[];
  /** Stack controls at full width when their own form container is narrow. */
  readonly stackOnNarrow?: boolean;
}
export type FormLabelResolver = (token: string, fallback: string) => string;
export type FormIssues = Record<string, string>;
export type GeneratedFormValues = Record<string, unknown>;
// Code-installed presentation overrides cannot add fields or replace validation.
export type FormOverrides = Record<string, { label?: string; control?: "text" | "textarea" }>;

function wireValue(field: GeneratedFormField, value: unknown): unknown {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    if (field.dateSemantics === "calendar-date") return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
    if (field.dateSemantics === "utc-instant") return value.toISOString();
  }
  return value;
}

export function visibleGeneratedFields(definition: GeneratedFormDefinition, values: GeneratedFormValues): readonly GeneratedFormField[] {
  const visible = new Set<string>();
  return definition.fields.filter(field => {
    const condition = field.visibleWhen;
    const parent = condition ? definition.fields.find(item => item.name === condition.field) : undefined;
    if (condition && (!parent || !visible.has(parent.name) || wireValue(parent, values[parent.name]) !== condition.equals)) return false;
    visible.add(field.name);
    return true;
  });
}

export function validateGeneratedForm(definition: GeneratedFormDefinition, values: GeneratedFormValues): FormIssues {
  if (definition.version !== 1) throw new Error(`Unsupported form contract version: ${definition.version}`);
  const issues: FormIssues = {};
  for (const f of visibleGeneratedFields(definition, values)) {
    const value = wireValue(f, values[f.name]);
    const required = f.required || !!f.requiredWhen && values[f.requiredWhen.field] === f.requiredWhen.equals;
    const absent = value === undefined || value === null || typeof value === "string" && !value.trim();
    if (absent) { if (required) issues[f.name] = "Enter a value."; continue; }
    if (f.type === "string" && typeof value !== "string" || f.type === "boolean" && typeof value !== "boolean"
      || ["number", "integer"].includes(f.type) && (typeof value !== "number" || !Number.isFinite(value) || f.type === "integer" && !Number.isInteger(value))) {
      issues[f.name] = "Enter a valid value."; continue;
    }
    if (typeof value === "string" && (f.minLength !== undefined && value.length < f.minLength || f.maxLength !== undefined && value.length > f.maxLength)) issues[f.name] = `Use ${f.minLength ?? 0}–${f.maxLength ?? "any"} characters.`;
    if (typeof value === "number" && (f.minimum !== undefined && value < f.minimum || f.maximum !== undefined && value > f.maximum)) issues[f.name] = "Enter a value within the allowed range.";
    if (f.options && !f.options.some(o => o.value === value && o.disabled !== true)) issues[f.name] = "Choose an available value.";
    if (f.dateSemantics === "calendar-date" && (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000-') || Number.isNaN(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) issues[f.name] = "Enter a calendar date (YYYY-MM-DD).";
    if (f.dateSemantics === "utc-instant" && (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value) || Number.isNaN(Date.parse(value)) || new Date(value).toISOString().slice(0, 19) !== value.slice(0, 19))) issues[f.name] = "Enter a UTC time, for example 2026-10-04T12:00:00Z.";
  }
  return issues;
}

/** Only generated editable paths are copied; transport identity cannot come from form values. */
export function editableFormPayload(definition: GeneratedFormDefinition, values: GeneratedFormValues): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const f of visibleGeneratedFields(definition, values)) {
    const value = wireValue(f, values[f.name]);
    if (value === undefined) continue;
    const [root, child] = f.name.split(".");
    if (child) { const nested = (result[root] ??= {}) as Record<string, unknown>; nested[child] = value; }
    else result[root] = value;
  }
  return result;
}

export function generatedInputConfig(definition: GeneratedFormDefinition, label: FormLabelResolver = (_, fallback) => fallback, overrides: FormOverrides = {}, selectedValues: GeneratedFormValues = {}): InputConfig[] {
  if (definition.version !== 1) throw new Error(`Unsupported form contract version: ${definition.version}`);
  for (const key of Object.keys(overrides)) if (!definition.fields.some(f => f.name === key)) throw new Error(`Unknown editable field: ${key}`);
  return definition.fields.map((f, index) => {
    const override = overrides[f.name];
    if (override?.control && !["text", "textarea"].includes(f.control)) throw new Error(`Unsupported control override: ${f.name}`);
    if (f.width !== undefined && !["1x", "2x", "3x", "4x", "5x", "fill"].includes(f.width)
      || f.row !== undefined && (!Number.isSafeInteger(f.row) || f.row < 1)) throw new Error(`Unsupported field layout: ${f.name}`);
    const options: InputSelectOption[] | undefined = f.options?.map(o => ({ value: o.value, viewValue: label(o.labelToken, o.label), ...(o.disabled === true ? { disabled: true } : {}) }));
    const selected = selectedValues[f.name];
    if (f.control === 'select' && options && selected !== undefined && selected !== null && selected !== ''
      && ['string', 'number', 'boolean'].includes(typeof selected) && !options.some(option => option.value === selected))
      options.push({ value: selected, viewValue: `${String(selected)} (unavailable)`, disabled: true });
    return { name: f.name, type: (override?.control ?? f.control) as InputConfig["type"], width: f.width ?? "fill", row: f.row ?? index + 1,
      label: override?.label ?? label(f.labelToken, f.label), maxLength: f.maxLength,
      readOnly: f.readOnly,
      ...(f.disabled === true ? { disabled: true } : {}),
      placeholder: f.dateSemantics === "utc-instant" ? "YYYY-MM-DDTHH:mm:ssZ" : undefined,
      options,
      validation: [...(f.required && f.type !== 'boolean' ? [Validators.required] : []), control => {
        const problems = validateGeneratedForm(definition, { ...(control.parent?.value ?? {}), [f.name]: control.value });
        return problems[f.name] ? { schema: true } : null;
      }], validationMessages: { schema: "Check this value against the field requirements." } };
  });
}

export function serverFormIssues(definition: GeneratedFormDefinition, error: { code?: string; problemCode?: string; issues?: { code: string; detail?: string }[]; detail?: string; message?: string; errors?: Record<string, string[]> }): FormIssues {
  if (definition.version !== 1) throw new Error(`Unsupported form contract version: ${definition.version}`);
  const issues: FormIssues = {};
  for (const f of definition.fields) {
    const messages = error.errors?.[f.name] ?? error.errors?.[f.name[0].toUpperCase() + f.name.slice(1)];
    if (Array.isArray(messages)) issues[f.name] = messages.join(" ");
    else {
      const issue = error.issues?.find(i => f.errorCodes.includes(i.code));
      const code = error.problemCode ?? error.code;
      if (issue || code && f.errorCodes.includes(code)) issues[f.name] = issue?.detail ?? error.detail ?? error.message ?? "The server rejected this value.";
    }
  }
  return issues;
}
